import dayjs from 'dayjs';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const NotificationType = 'error' | 'success' | 'warning' | 'notification';

/**
 * @typedef {Object} NotificationStore
 * @property {import('@/types/NotificationItem').NotificationItem[]} notifications
 * @property {(title: string, message: string, type: NotificationType, ttl?: number) => void} addNotification
 * @property {(id: string) => void} removeNotification
 * @property {() => void} clearExpired
 * @property {() => import('@/types/NotificationItem').NotificationItem[]} getNotifications
 */


/**
 * @type {import("zustand").StoreApi<NotificationStore>}
 */
export const useNotificationStore = create(
  persist(
    (set, get) => ({
      notifications: [],

      /**
       * Adds a new notification to the store.
       *
       * @param title - The title of the notification.
       * @param message - The message content of the notification.
       * @param type - The type of the notification, indicating its importance or category.
       * @param ttl - Time to live in milliseconds for the notification. Defaults to 600,000 ms (10 minutes).
       */
      addNotification: (title, message, type, ttl = 600_000 * 10) => {
        const now = Date.now();
        const generateId = () => {
          if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
            return crypto.randomUUID();
          }
          return `notification-${now}-${Math.random().toString(16).slice(2)}`;
        };
        const newNotification = {
          id: generateId(),
          title,
          message,
          type,
          timestamp: dayjs().toISOString(),
          expiresAt: now + ttl,
        };

        console.log("[DEBUG useNotificationStore] addNotification called:", { 
          newNotification, 
          currentCount: get().notifications.length,
          storeInstance: "instance-1"
        });

        set({ notifications: [...get().notifications, newNotification] });
        
        console.log("[DEBUG useNotificationStore] Notification added, new count:", get().notifications.length);
      },

      removeNotification: (id) => {
        set({ notifications: get().notifications.filter(n => n.id !== id) });
      },

      clearExpired: () => {
        const now = Date.now();
        set({ notifications: get().notifications.filter(n => !n.expiresAt || n.expiresAt > now) });
      },

      getNotifications: () => get().notifications,
    }),
    {
      name: 'notification-storage',
      onRehydrateStorage: (state) => {
        console.log("[DEBUG useNotificationStore] Storage rehydrated:", state);
        return (state, error) => {
          if (error) {
            console.error("[DEBUG useNotificationStore] Rehydrate error:", error);
          } else {
            console.log("[DEBUG useNotificationStore] Rehydrate success, notifications:", state?.notifications?.length || 0);
          }
        };
      },
    }
  )
);